import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { alpha } from '@mui/material/styles';
import {
  Grid,
  TableColumnVisibility,
  TableHeaderRow,
  TableSummaryRow,
  VirtualTable,
} from '@devexpress/dx-react-grid-material-ui';
import {
  CustomSummary,
  IntegratedSorting,
  SortingState,
  SortingStateProps,
  SummaryState,
} from '@devexpress/dx-react-grid';
import {
  buidTotalSummaryItems,
  buildActions,
  buildColumnExtensions,
  buildColumns,
  buildRows,
  buildSorting,
} from './adapter';
import { useDataGridFlexSales } from './DataGridFlexSalesProvider';
import { Row } from './DataGridHandler';
import {
  ESTIMATED_ROW_HEIGHT,
  GridRoot,
  VirtualTabledRow,
  TableHeaderRowRow,
  VirtualTableNoDataCell,
  TableSummaryRowTotalCell,
  VirtualTableCell,
  ActionColumns,
} from './devExpressCustom';
import { VirtualTableHeaderCell } from './devExpressCustom/Cells';
import { EmptyState } from '../Visual';

const DataGridContainer = styled.div`
  position: relative;
  height: 100%;
  min-height: 360px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: ${({ theme }) => theme.spacing(0, 0, 2.5, 2.5)};
  background: ${({ theme }) => theme.customTokens.surfaces.dataGridHeaderGradient};
  box-shadow: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? `0 18px 50px ${alpha(theme.palette.common.black, 0.28)}`
      : `0 18px 50px ${alpha(theme.palette.text.primary, 0.08)}`};

  .MuiTable-root {
    border-collapse: separate;
  }

  .MuiTableCell-root {
    border-bottom-color: ${({ theme }) => theme.palette.divider};
  }
`;

const EmptyStateOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(4)};
  pointer-events: none;
`;

const EmptyStateContent = styled.div`
  width: min(720px, 100%);
  pointer-events: auto;
`;

const MIN_EMPTY_COLUMN_WIDTH = 84;
const MIN_EMPTY_SORTABLE_COLUMN_WIDTH = 108;
const SMALL_EMPTY_COLUMN_KEEP_BASE_WIDTH_THRESHOLD = 120;
const EMPTY_HEADER_HORIZONTAL_PADDING = 20;
const EMPTY_HEADER_SORT_ICON_WIDTH = 24;
const EMPTY_HEADER_TEXT_SAFETY_BUFFER = 8;

let measurementCanvas: HTMLCanvasElement | null = null;

const getHeaderTitleWidth = (title: string) => {
  if (!title) return 0;
  if (typeof document === 'undefined') return title.length * 8;

  if (!measurementCanvas) measurementCanvas = document.createElement('canvas');
  const context = measurementCanvas.getContext('2d');
  if (!context) return title.length * 8;

  const bodyStyles = getComputedStyle(document.body);
  const fontFamily = bodyStyles.fontFamily || 'sans-serif';

  // Approximate MUI subtitle2 used in table headers to avoid overestimating width.
  context.font = `500 14px ${fontFamily}`;
  const measured = context.measureText(title).width;
  const fallback = title.length * 8;

  return Math.max(measured, fallback);
};

const totalValues: number[] = []; // No se utiliza, pero el componente CustomSummary requiere que se pase dicho valor

interface DataGridProps {
  showSummaryRow?: boolean;
}

const DataGrid = ({ showSummaryRow = true }: DataGridProps) => {
  const { handler } = useDataGridFlexSales();
  const { t } = useTranslation();
  const [columnsHidden, setColumnsHidden] = useState([...handler.columnsHidden]);
  const [rows, setRows] = useState(buildRows(handler.getRows()));
  const [loading, setLoading] = useState(handler.loading);
  const [columns, setColumns] = useState(buildColumns(handler.columns, handler.actions));
  const [totalSummaryItems, setTotalSummaryItems] = useState(buidTotalSummaryItems(handler.totals));
  const [sorting, setSorting] = useState(buildSorting(handler.sorting));
  const [tableColumnExtensions, setTableColumnExtensions] = useState(
    buildColumnExtensions(handler.columns)
  );

  useEffect(() => {
    const refreshRows = (rows: Row<unknown>[]) => {
      setRows(buildRows(rows));
      setTotalSummaryItems(buidTotalSummaryItems(handler.totals));
    };

    const listenerLoading = handler.addEventListener('loading', ({ loading: isLoading }) =>
      setLoading(isLoading)
    );

    const listenerColumnsHidden = handler.addEventListener('columnsHidden', ({ columnsHidden }) =>
      setColumnsHidden(columnsHidden)
    );

    const listenerContext = handler.addEventListener('context', ({ rows, columns }) => {
      setColumns(buildColumns(columns, handler.actions));
      setTableColumnExtensions(buildColumnExtensions(columns));
      refreshRows(rows);
    });

    const listenerColumnConfig = handler.addEventListener('columnsConfig', ({ rows, columns }) => {
      setColumns(buildColumns(columns, handler.actions));
      setTableColumnExtensions(buildColumnExtensions(columns));
      refreshRows(rows);
    });

    const listenerRows = handler.addEventListener('rows', ({ rows }) => refreshRows(rows));

    return () => {
      listenerLoading.dispose();
      listenerColumnsHidden.dispose();
      listenerContext.dispose();
      listenerRows.dispose();
      listenerColumnConfig.dispose();
    };
  }, [handler]);

  const sortingChangeHandle = useCallback<NonNullable<SortingStateProps['onSortingChange']>>(
    ([item]) => {
      handler.setSorting(item.columnName, item.direction === 'asc');
      setSorting([item]);
    },
    [handler]
  );

  const actions = useMemo(() => buildActions(handler.actions), [handler]);
  const isEmpty = !loading && rows.length === 0;
  const baseColumnExtensions = useMemo(() => tableColumnExtensions || [], [tableColumnExtensions]);

  const emptyColumnExtensions = useMemo(() => {
    const hiddenColumns = new Set(columnsHidden);
    const columnsByName = columns.reduce<Record<string, string>>(
      (result, column) => ({ ...result, [column.name]: String(column.title || '') }),
      {}
    );

    return baseColumnExtensions.map(extension => {
      const { columnName } = extension;

      if (hiddenColumns.has(columnName)) return extension;

      const title = columnsByName[columnName] || '';
      const isActionColumn = title.trim().length === 0;
      if (isActionColumn) return extension;

      const originalWidth = typeof extension.width === 'number' ? extension.width : undefined;

      const measuredTitleWidth = getHeaderTitleWidth(title);
      const desiredWidth = Math.ceil(
        measuredTitleWidth +
          EMPTY_HEADER_HORIZONTAL_PADDING +
          EMPTY_HEADER_SORT_ICON_WIDTH +
          EMPTY_HEADER_TEXT_SAFETY_BUFFER
      );

      const minWidthFloor =
        originalWidth && originalWidth <= SMALL_EMPTY_COLUMN_KEEP_BASE_WIDTH_THRESHOLD
          ? originalWidth
          : MIN_EMPTY_COLUMN_WIDTH;

      const minSortableWidth = Math.max(minWidthFloor, MIN_EMPTY_SORTABLE_COLUMN_WIDTH);

      return {
        ...extension,
        width: Math.max(minSortableWidth, desiredWidth),
      };
    });
  }, [baseColumnExtensions, columns, columnsHidden]);

  const virtualTableColumnExtensions = isEmpty ? emptyColumnExtensions : baseColumnExtensions;

  return (
    <DataGridContainer data-tour="report-results">
      <Grid rows={rows} columns={columns} rootComponent={GridRoot}>
        <SummaryState totalItems={totalSummaryItems} />
        <SortingState sorting={sorting} onSortingChange={sortingChangeHandle} />
        <IntegratedSorting />
        <CustomSummary totalValues={totalValues} />
        <VirtualTable
          height="100%"
          estimatedRowHeight={ESTIMATED_ROW_HEIGHT}
          cellComponent={VirtualTableCell}
          rowComponent={VirtualTabledRow}
          columnExtensions={virtualTableColumnExtensions}
          noDataCellComponent={VirtualTableNoDataCell}
        />
        <TableHeaderRow
          cellComponent={VirtualTableHeaderCell}
          rowComponent={TableHeaderRowRow}
          showSortingControls
        />
        <TableColumnVisibility hiddenColumnNames={columnsHidden} />
        {showSummaryRow && <TableSummaryRow totalCellComponent={TableSummaryRowTotalCell} />}
        <ActionColumns actions={actions} />
      </Grid>
      {isEmpty && (
        <EmptyStateOverlay>
          <EmptyStateContent>
            <EmptyState title={t('dataGridFlexSales.labels.noRows')} />
          </EmptyStateContent>
        </EmptyStateOverlay>
      )}
    </DataGridContainer>
  );
};

export default DataGrid;
